import React, { useState } from 'react';
import { Box, Typography, Container, Grid, Button, Checkbox, Chip, IconButton, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import DataTable, { Column, RowAction } from '../components/DataTable';
import EPRCViewerDrawer from '../components/EPRCViewerDrawer';
import BulkEPRCModal from '../components/BulkEPRCModal';
import { useToast } from '../components/ui/ToastProvider';
import { VALID_MTOS } from '../utils/constants';
interface EPRCGenerationProps {
  onLogout: () => void;
}
interface Transaction {
  id: string;
  mtoRef: string;
  bankRef: string;
  mtoName: string;
  remitterName: string;
  beneficiaryName: string;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  payoutType: 'FT' | 'RAAST' | 'ONELINK' | 'RTGS';
  createdAt: string;
  ePRCNo?: string;
}
const drawerWidth = 240;
const MOCK_COMPLETED_TRANSACTIONS: Transaction[] = Array(50).fill(null).map((_, index) => ({
  id: `TXN-${1000 + index}`,
  mtoRef: `MTO-REF-${2000 + index}`,
  bankRef: `BNK-${3000 + index}`,
  mtoName: VALID_MTOS[index % VALID_MTOS.length],
  remitterName: ['John Smith', 'Sarah Johnson', 'Ahmed Khan', 'Maria Garcia', 'Li Wei'][index % 5],
  beneficiaryName: ['Ali Hassan', 'Fatima Ahmed', 'Muhammad Bilal', 'Ayesha Khan', 'Usman Ali'][index % 5],
  amountFCY: (500 + index * 10).toFixed(2),
  currencyFCY: ['USD', 'EUR', 'GBP', 'AED', 'SAR'][index % 5],
  amountPKR: ((500 + index * 10) * 280).toFixed(2),
  payoutType: ['FT', 'RAAST', 'ONELINK', 'RTGS'][index % 4] as Transaction['payoutType'],
  createdAt: new Date(Date.now() - index * 3600000).toISOString(),
  ePRCNo: `EPRC-2024-${String(10000 + index).padStart(6, '0')}`
}));
export default function EPRCGeneration({
  onLogout
}: EPRCGenerationProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [transactions] = useState<Transaction[]>(MOCK_COMPLETED_TRANSACTIONS);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [eprcDrawerOpen, setEprcDrawerOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [bulkEPRCModalOpen, setBulkEPRCModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    showToast
  } = useToast();
  const handleSelectTransaction = (transactionId: string) => {
    const next = new Set(selectedTransactions);
    if (next.has(transactionId)) next.delete(transactionId);else next.add(transactionId);
    setSelectedTransactions(next);
  };
  const handleSelectAll = (checked: boolean) => {
    setSelectedTransactions(checked ? new Set(transactions.map(t => t.id)) : new Set());
  };
  const handleViewEPRC = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setEprcDrawerOpen(true);
  };
  const handleDownloadEPRC = (transaction: Transaction) => {
    showToast(`Downloading e-PRC for ${transaction.ePRCNo}`, 'success', 'Download Started');
  };
  const handleViewBulkEPRC = () => {
    if (selectedTransactions.size === 0) {
      showToast('Please select at least one transaction', 'warning');
      return;
    }
    setBulkEPRCModalOpen(true);
  };
  const columns: Column<Transaction>[] = [{
    id: 'select',
    label: <Checkbox checked={selectedTransactions.size === transactions.length && transactions.length > 0} indeterminate={selectedTransactions.size > 0 && selectedTransactions.size < transactions.length} onChange={e => handleSelectAll(e.target.checked)} data-id="element-3533" data-mp="2sdpvtiv3rl0o" />,
    renderCell: row => <Checkbox checked={selectedTransactions.has(row.id)} onChange={() => handleSelectTransaction(row.id)} data-id="element-3534" data-mp="2nly1qycqmx5s" />
  }, {
    id: 'id',
    label: 'Transaction ID'
  }, {
    id: 'mtoRef',
    label: 'MTO Reference'
  }, {
    id: 'bankRef',
    label: 'Bank Reference'
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Chip label={row.mtoName} size="small" sx={{
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      color: '#0EA5E9',
      fontWeight: 700
    }} data-id="element-3535" data-mp="1sfh1f049l84k" />
  }, {
    id: 'remitterName',
    label: 'Remitter'
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary'
  }, {
    id: 'amountFCY',
    label: 'Amount (FCY)',
    renderCell: row => <Typography variant="body2" fontWeight={700} data-id="element-3536" data-mp="1dizmy00s3dds">{row.currencyFCY} {row.amountFCY}</Typography>
  }, {
    id: 'amountPKR',
    label: 'Amount (PKR)',
    renderCell: row => <Typography variant="body2" fontWeight={700} data-id="element-3537" data-mp="265kfv4wwjxjg">PKR {row.amountPKR}</Typography>
  }, {
    id: 'payoutType',
    label: 'Payout Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : row.payoutType === 'RAAST' ? 'rgba(139, 92, 246, 0.1)' : row.payoutType === 'ONELINK' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : row.payoutType === 'RAAST' ? '#8B5CF6' : row.payoutType === 'ONELINK' ? '#F59E0B' : '#10B981',
      fontWeight: 700
    }} data-id="element-3538" data-mp="1wgam7kmeu6t8" />
  }, {
    id: 'ePRCNo',
    label: 'e-PRC Number',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" data-id="element-3539" data-mp="36gdaenlkfkd5">{row.ePRCNo}</Typography>
  }, {
    id: 'receipt',
    label: 'Receipt',
    renderCell: row => <Box sx={{
      display: 'flex',
      gap: 1
    }} data-id="element-3540" data-mp="3gc8185zy0lr0">
          <Tooltip title="View Receipt" data-id="element-3541" data-mp="3kdjrmdqn5b09">
            <IconButton size="small" onClick={e => {
          e.stopPropagation();
          handleViewEPRC(row);
        }} data-id="element-3542" data-mp="3kcihqcld2uk4"><Icon icon="lucide:file-text" width={18} height={18} data-id="element-3543" data-mp="3p634f8hi8240" /></IconButton>
          </Tooltip>
          <Tooltip title="Download Receipt" data-id="element-3544" data-mp="3u2jvobk02wlb">
            <IconButton size="small" onClick={e => {
          e.stopPropagation();
          handleDownloadEPRC(row);
        }} data-id="element-3545" data-mp="2xk3gtnaltry"><Icon icon="lucide:download" width={18} height={18} data-id="element-3546" data-mp="2vqvnb0y13id" /></IconButton>
          </Tooltip>
        </Box>
  }, {
    id: 'createdAt',
    label: 'Date',
    renderCell: row => new Date(row.createdAt).toLocaleDateString()
  }];
  const rowActions: RowAction<Transaction>[] = [];
  const filteredTransactions = transactions.filter(txn => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return [txn.id, txn.mtoRef, txn.bankRef, txn.mtoName, txn.remitterName, txn.beneficiaryName, txn.ePRCNo || ''].some(value => value.toLowerCase().includes(query));
  });
  return <Box sx={{
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    bgcolor: 'background.default'
  }} data-id="element-3547" data-mp="3s5k5cjtozkui">
      <TopBar onMenuClick={() => setMobileOpen(!mobileOpen)} onLogout={onLogout} data-id="element-3548" data-mp="awwssvvp2vcw" />
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage="eprc-generation" data-id="element-3549" data-mp="auu3ke2d9698" />

      <Box component="main" sx={{
      flexGrow: 1,
      width: {
        xs: '100%',
        sm: `calc(100% - ${drawerWidth}px)`
      },
      maxWidth: '100%',
      ml: 0,
      mt: 8,
      py: 2,
      px: {
        xs: 2,
        sm: 3,
        md: 5
      },
      overflow: 'hidden',
      bgcolor: 'background.default'
    }} data-id="element-3550" data-mp="2cojovbug1gg8">
        <Container maxWidth={false} disableGutters data-id="element-3551" data-mp="s57k4kub3lik">
          <Grid container spacing={2} alignItems="center" sx={{
          mb: 3
        }} data-id="element-3552" data-mp="dhmjqyotg0ec">
            <Grid item xs={12} md={6} data-id="element-3553" data-mp="dfidia3g2paw">
              <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-3554" data-mp="ig626p47au1y">
                <Icon icon="lucide:file-text" width={24} height={24} color="#0EA5E9" data-id="element-3555" data-mp="iecx3e98khw2" />
                <Typography variant="h5" component="h1" sx={{
                ml: 1.5,
                fontWeight: 700
              }} data-id="element-3556" data-mp="4j1173jcgldk">e-PRC Generation</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} data-id="element-3557" data-mp="3qoxvdkyhoi8g">
              <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: {
                xs: 'flex-start',
                md: 'flex-end'
              }
            }} data-id="element-3558" data-mp="3qq929tn8e63s">
                {selectedTransactions.size > 0 && <>
                    <Button variant="outlined" startIcon={<Icon icon="lucide:x" width={18} height={18} data-id="element-3560" data-mp="1twebd9e513ml" />} onClick={() => setSelectedTransactions(new Set())} data-id="element-3559" data-mp="1ouovngfz01s6">Clear ({selectedTransactions.size})</Button>
                    <Button variant="contained" startIcon={<Icon icon="lucide:file-stack" width={18} height={18} data-id="element-3562" data-mp="cmwjzweb44b6" />} onClick={handleViewBulkEPRC} data-id="element-3561" data-mp="gr46dmxal868">View Bulk e-PRC ({selectedTransactions.size})</Button>
                  </>}
              </Box>
            </Grid>
          </Grid>

          <DataTable title="Completed Transactions" data={filteredTransactions} columns={columns} rowActions={rowActions} keyField="id" onSearchChange={setSearchQuery} searchValue={searchQuery} searchPlaceholder="Search by transaction ID, MTO ref, bank ref, e-PRC number..." emptyStateMessage="No completed transactions found" initialVisibleColumns={['select', 'id', 'mtoRef', 'bankRef', 'mtoName', 'remitterName', 'beneficiaryName', 'amountFCY', 'amountPKR', 'payoutType', 'ePRCNo', 'receipt', 'createdAt']} data-id="element-3563" data-mp="2xd0qpmom8fs" />

          {selectedTransaction && <EPRCViewerDrawer open={eprcDrawerOpen} onClose={() => {
          setEprcDrawerOpen(false);
          setSelectedTransaction(null);
        }} transaction={selectedTransaction as any} data-id="element-3564" data-mp="1lvroik4olu4a" />}
          <BulkEPRCModal open={bulkEPRCModalOpen} onClose={() => setBulkEPRCModalOpen(false)} transactions={transactions.filter(t => selectedTransactions.has(t.id)) as any} data-id="element-3565" data-mp="1vn5hsa61hxoz" />
        </Container>
      </Box>
    </Box>;
}